import { useCallback, useEffect, useState } from "react";
import { useAdminFetch } from "../lib/admin-api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";

/**
 * GBP Ambiguity Queue (PR #31). Admin-only. Surfaces licenses whose address-only
 * GBP backfill found 2+ Places within 30 m and left an OPEN
 * core.gbp_api_error_log row (error_code='ambiguous_multiple_in_radius'). For
 * each, the admin picks the correct candidate ("This one" → Place Details +
 * gbp_profile upsert) or rejects all ("None of these"); either resolves the row
 * and drops it from the queue.
 */

interface Candidate {
  place_id: string | null;
  display_name: string | null;
  distance_m: number | null;
  formatted_address: string | null;
  website: string | null;
  types: string[];
}

interface QueueItem {
  id: string;
  license_number: string;
  license_type: string | null;
  dba_name: string | null;
  legal_name: string | null;
  premise_street: string | null;
  premise_city: string | null;
  premise_zip: string | null;
  premise_lat: number | null;
  premise_lng: number | null;
  license_status: string | null;
  run_id: string | null;
  attempted_at: string;
  candidates: Candidate[];
}

function none(value: string | null | undefined) {
  const t = (value ?? "").trim();
  return t ? t : <span className="italic text-gray-400">(none)</span>;
}

export default function GbpAmbiguity() {
  const adminFetch = useAdminFetch();
  const toast = useToast();

  const [items, setItems] = useState<QueueItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyLicense, setBusyLicense] = useState<string | null>(null);
  const [manualInput, setManualInput] = useState<Record<string, string>>({});
  const [manualError, setManualError] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminFetch<{ items: QueueItem[]; total: number }>(
        `/api/admin/gbp/ambiguity-queue?limit=100&offset=0`
      );
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (err) {
      toast.push(`Failed to load ambiguity queue: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [adminFetch, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (license_number: string, place_id: string, label: string) => {
    setBusyLicense(license_number);
    try {
      await adminFetch(`/api/admin/gbp/resolve-ambiguity`, {
        method: "POST",
        body: JSON.stringify({ license_number, place_id }),
      });
      toast.push(`Resolved ${license_number} — ${label}`, "success");
      setItems((prev) => prev.filter((i) => i.license_number !== license_number));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      toast.push(`Resolve failed: ${(err as Error).message}`, "error");
    } finally {
      setBusyLicense(null);
    }
  };

  // Third action: admin pastes the correct Place when none of Google's
  // candidates fit. A value starting with http is a share URL (server resolves
  // it to a place_id); anything else is treated as a raw place_id (ChIJ…).
  const submitManual = async (license_number: string) => {
    const raw = (manualInput[license_number] ?? "").trim();
    if (!raw) return;
    setBusyLicense(license_number);
    setManualError((prev) => ({ ...prev, [license_number]: "" }));
    const payload = raw.toLowerCase().startsWith("http")
      ? { license_number, share_url: raw }
      : { license_number, place_id: raw };
    try {
      await adminFetch(`/api/admin/gbp/resolve-ambiguity`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      toast.push(`Resolved ${license_number} — manual Place`, "success");
      setItems((prev) => prev.filter((i) => i.license_number !== license_number));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      setManualError((prev) => ({ ...prev, [license_number]: (err as Error).message }));
    } finally {
      setBusyLicense(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">GBP Ambiguity Queue</h2>
        <p className="text-sm text-gray-600 mt-1">
          Licenses where the address-only backfill found more than one Google Business Profile within 30 m
          of the premise. Pick the correct storefront, or reject all candidates.
        </p>
      </div>

      <div className="text-xs text-gray-500">{total} open</div>

      {loading && items.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-600">
          No open ambiguities. 🎉
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item) => {
            const busy = busyLicense === item.license_number;
            return (
              <article key={item.id} className="rounded-lg border border-amber-200 bg-white p-5 shadow-sm">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-[18rem_1fr]">
                  <section className="rounded-md border border-gray-200 bg-gray-50/60 p-4">
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">License</h4>
                    <dl className="space-y-1.5">
                      <div>
                        <dt className="text-xs text-gray-500">DBA</dt>
                        <dd className="text-sm font-semibold text-gray-900">{none(item.dba_name)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Legal</dt>
                        <dd className="text-sm text-gray-700">{none(item.legal_name)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">Premise</dt>
                        <dd className="text-sm text-gray-700">
                          {none(item.premise_street)}
                          <div className="text-gray-500">
                            {[item.premise_city, item.premise_zip].filter(Boolean).join(" ") || "—"}
                          </div>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-500">License #</dt>
                        <dd className="font-mono text-xs text-gray-600">{item.license_number}</dd>
                      </div>
                    </dl>
                  </section>

                  <section>
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Candidate profiles ({item.candidates.length})
                      </h4>
                      <Button
                        variant="danger-ghost"
                        size="sm"
                        type="button"
                        disabled={busy}
                        onClick={() => resolve(item.license_number, "none", "none of these")}
                      >
                        None of these
                      </Button>
                    </div>
                    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {item.candidates.map((c, idx) => (
                        <li
                          key={c.place_id ?? idx}
                          className="flex flex-col rounded-md border border-gray-200 p-3"
                        >
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-900">
                              {c.display_name || <span className="italic text-gray-400">(unnamed)</span>}
                            </div>
                            {c.distance_m != null && (
                              <div className="text-xs text-gray-500">{c.distance_m} m away</div>
                            )}
                            {c.formatted_address && (
                              <div className="text-xs text-gray-500">{c.formatted_address}</div>
                            )}
                            {c.website && (
                              <a
                                href={c.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-accent-700 hover:underline break-all"
                              >
                                {c.website}
                              </a>
                            )}
                            <div className="mt-1 font-mono text-[10px] text-gray-400 break-all">{c.place_id}</div>
                          </div>
                          <Button
                            variant="primary"
                            size="sm"
                            type="button"
                            className="mt-2 self-start"
                            disabled={busy || !c.place_id}
                            onClick={() => c.place_id && resolve(item.license_number, c.place_id, c.display_name || c.place_id)}
                          >
                            This one
                          </Button>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 border-t border-gray-100 pt-3">
                      <label className="block text-xs">
                        <span className="mb-1 block font-medium text-gray-600">
                          I have the correct Place
                        </span>
                        <div className="flex gap-2">
                          <input
                            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm"
                            value={manualInput[item.license_number] ?? ""}
                            onChange={(e) =>
                              setManualInput((prev) => ({ ...prev, [item.license_number]: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                submitManual(item.license_number);
                              }
                            }}
                            placeholder="Paste a Google Maps share link or a place_id (ChIJ…)"
                            disabled={busy}
                          />
                          <Button
                            variant="secondary"
                            size="sm"
                            type="button"
                            className="shrink-0"
                            disabled={busy || !(manualInput[item.license_number] ?? "").trim()}
                            onClick={() => submitManual(item.license_number)}
                          >
                            {busy ? "Resolving…" : "Use this Place"}
                          </Button>
                        </div>
                        <span className="mt-1 block text-[11px] text-gray-400">
                          Paste any Google Maps share link (from the 'Share' button in Maps) or a place_id starting with ChIJ.
                        </span>
                      </label>
                      {manualError[item.license_number] && (
                        <div className="mt-1 text-xs text-red-600">
                          {manualError[item.license_number]}
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
